'use client';

import { type HTMLAttributes, useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { BookOpen, Eye } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { APP_ROUTES, AUTH_ROUTES } from '@/app/routes';
import { Button } from '@/atoms/Button/Button';
import { Container } from '@/atoms/Container/Container';
import { LANDING_HERO_SECTION_ID, LANDING_NEXT_SECTION_ID } from '@/templates/Public/Landing/Landing.constants';
import { HeaderSocialLinks } from '../Header/Header';
import { HeaderButtonSignIn } from '../HeaderButtonSignIn/HeaderButtonSignIn';
import { PassportLogin } from '../PassportLogin/PassportLogin';

export const HeaderHome = (props: HTMLAttributes<HTMLDivElement>) => {
  const router = useRouter();
  const pathname = usePathname();
  const isLandingPage = pathname === '/';
  const [showLogin, setShowLogin] = useState(false);
  useEffect(() => {
    setShowLogin(false);
    if (!isLandingPage || typeof IntersectionObserver === 'undefined') return;
    const hero = document.getElementById(LANDING_HERO_SECTION_ID);
    if (!hero) return;
    const observer = new IntersectionObserver(([entry]) => setShowLogin(!entry.isIntersecting), {
      rootMargin: '-30% 0px -35% 0px',
      threshold: 0,
    });
    observer.observe(hero);
    return () => observer.disconnect();
  }, [isLandingPage]);
  const showLearnExplore = isLandingPage || pathname === AUTH_ROUTES.LOGOUT;
  const handleLearn = () => {
    if (isLandingPage) document.getElementById(LANDING_NEXT_SECTION_ID)?.scrollIntoView({ behavior: 'smooth' });
    else router.push(`/#${LANDING_NEXT_SECTION_ID}`);
  };
  return (
    <Container className="flex-1 flex-row items-center justify-end gap-3" {...props}>
      {showLearnExplore ? (
        <>
          <Button
            id="header-learn-btn"
            data-testid="header-learn-btn"
            variant="outline"
            onClick={handleLearn}
            className="hidden gap-2 md:inline-flex"
          >
            <BookOpen className="size-4" />
            {'Learn'}
          </Button>
          <Button
            id="header-explore-btn"
            data-testid="header-explore-btn"
            variant="outline"
            onClick={() => router.push(APP_ROUTES.HOME)}
            className="hidden gap-2 md:inline-flex"
          >
            <Eye className="size-4" />
            {'Explore'}
          </Button>
        </>
      ) : (
        <HeaderSocialLinks />
      )}
      <AnimatePresence initial={false}>
        {isLandingPage && showLogin && (
          <motion.div
            key="landing-login"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="flex shrink-0 items-center"
          >
            <PassportLogin compact />
          </motion.div>
        )}
      </AnimatePresence>
      {!isLandingPage && pathname !== AUTH_ROUTES.SIGN_IN && <HeaderButtonSignIn />}
    </Container>
  );
};
